import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-orange-50 py-12 px-4">
      <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-lg p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
        <p className="text-gray-500 mb-8">Last updated: June 30, 2026</p>

        <div className="space-y-6 text-gray-700">
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Introduction</h2>
            <p>
              Welcome to HappyBook ("we," "our," or "us"). HappyBook is a positive,
              motivational social network where people share uplifting content, set goals,
              practice gratitude, and support one another. We are committed to protecting your
              privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard
              your information when you use the HappyBook mobile application and website (the "Service").
              By using HappyBook, you agree to the practices described in this policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Information We Collect</h2>

            <h3 className="font-semibold mt-4 mb-2">2.1 Information You Provide</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Account Information:</strong> Name, email address, and password (passwords are stored only as a secure hash).</li>
              <li><strong>Profile Information:</strong> Profile photo, bio, and any details you choose to add.</li>
              <li><strong>Content You Create:</strong> Posts, photos, comments, reactions, stories, reels, goals, gratitude journal entries, planner tasks and habits, and wellness activity.</li>
              <li><strong>Messages:</strong> Direct messages and conversations you send to other users.</li>
              <li><strong>AI Interactions:</strong> Prompts and messages you send to our "Joy" AI assistant, and text or photos you submit to AI Camera and read-aloud features.</li>
              <li><strong>Payment Information:</strong> If you purchase a post "boost"/advertising package, payment is processed by Stripe. We do not store your full card details on our servers.</li>
            </ul>

            <h3 className="font-semibold mt-4 mb-2">2.2 Information Collected Automatically</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Device Information:</strong> Device type, operating system, and app version.</li>
              <li><strong>Usage &amp; Analytics Data:</strong> Features used, screens viewed, and interaction patterns, used to improve the Service.</li>
              <li><strong>Log Data:</strong> IP address and access times for security and reliability.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">3. Device Permissions</h2>
            <p>With your consent, the mobile app may request the following permissions. You can grant or deny each, and change them anytime in your device settings:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li><strong>Photos / Media Library:</strong> To let you choose images for posts, stories, your profile photo, and AI Camera.</li>
              <li><strong>Camera:</strong> To capture photos for posts and AI Camera (if you choose to use the camera).</li>
              <li><strong>Microphone:</strong> To enable voice features such as speech-to-text, when you choose to use them.</li>
            </ul>
            <p className="mt-2">We only access these after you take an action that requires them, and only for the stated purpose.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">4. How We Use Your Information</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>To provide, maintain, and personalize the Service.</li>
              <li>To display your content and connect you with other members.</li>
              <li>To power AI features (positivity moderation, the Joy assistant, AI Camera, affirmations, and read-aloud).</li>
              <li>To track progress, streaks, points, and achievements.</li>
              <li>To process advertising/boost payments.</li>
              <li>To send you optional updates, summaries, or notifications.</li>
              <li>To keep the community safe and prevent fraud or abuse.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">5. AI-Powered Features</h2>
            <p>HappyBook uses artificial intelligence to enhance your experience:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li><strong>Content Moderation:</strong> AI helps review posts to keep the community positive.</li>
              <li><strong>Joy AI Assistant:</strong> A chat companion that offers encouragement, advice, and motivation.</li>
              <li><strong>AI Camera:</strong> Generates or enhances inspirational images from your prompts or photos.</li>
              <li><strong>Wellness &amp; Affirmations:</strong> Personalizes affirmations and breathing exercises.</li>
              <li><strong>Read Aloud (Text-to-Speech):</strong> Converts post text to audio on request.</li>
            </ul>
            <p className="mt-2">
              To provide these features, relevant content is sent to trusted AI providers (such as Google Gemini and OpenAI)
              for processing. This data is used only to deliver the feature and is <strong>not sold</strong> to third parties.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Data Sharing</h2>
            <p>We do <strong>NOT</strong> sell your personal information. We share data only in these cases:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li><strong>Publicly, With Your Choice:</strong> Content you post is visible to other users; messages are visible to their recipients.</li>
              <li><strong>Service Providers:</strong> Trusted partners that help operate the Service (cloud hosting, database, analytics, email delivery, AI processing, and payments).</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect the rights, property, or safety of our users and the public.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Data Security</h2>
            <p>We use industry-standard measures to protect your data, including:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Encrypted data transmission (HTTPS/TLS).</li>
              <li>Secure password hashing.</li>
              <li>Authentication tokens and access controls.</li>
            </ul>
            <p className="mt-2">No method of transmission or storage is 100% secure, but we work to protect your information.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Your Rights &amp; Choices</h2>
            <p>You have the right to:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li><strong>Access:</strong> Request a copy of your data.</li>
              <li><strong>Correction:</strong> Update or correct your information from your profile.</li>
              <li><strong>Deletion:</strong> Delete your content or your entire account (see Section 9).</li>
              <li><strong>Opt-out:</strong> Decline device permissions or disable optional notifications.</li>
            </ul>
            <p className="mt-2">To exercise these rights, contact us at tomaceo1983@gmail.com.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Account &amp; Data Deletion</h2>
            <p>
              You can request deletion of your account and associated personal data at any time by:
            </p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Using the account deletion option in the app settings, or</li>
              <li>Emailing <strong>tomaceo1983@gmail.com</strong> from your registered email address with the subject "Delete My Account."</li>
            </ul>
            <p className="mt-2">
              Upon a verified request, we will delete your personal data within 30 days, except where retention is
              required by law or for legitimate business purposes (such as fraud prevention or completed transaction records).
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">10. Children's Privacy</h2>
            <p>
              HappyBook is not intended for children under 13 (or the minimum age required in your country).
              We do not knowingly collect personal information from children. If you believe a child has provided
              us with personal information, please contact us and we will delete it.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">11. Data Retention</h2>
            <p>
              We retain your data for as long as your account is active. After account deletion, we remove your
              personal data within 30 days, except where longer retention is legally required.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">12. Third-Party Services</h2>
            <p>HappyBook relies on the following third-party services, each governed by its own privacy policy:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li><strong>Stripe</strong> — payment processing for advertising/boost features.</li>
              <li><strong>Google Gemini</strong> — AI assistant and content features.</li>
              <li><strong>OpenAI</strong> — AI image generation, text-to-speech, and speech-to-text.</li>
              <li><strong>Cloud hosting, database, and email providers</strong> — to operate the Service and send reports.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">13. International Users</h2>
            <p>
              Your information may be processed and stored in countries other than your own. By using HappyBook,
              you consent to the transfer of your information to these locations, which may have different data
              protection laws than your country of residence.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">14. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of significant changes
              through the app or via email. Continued use of HappyBook after changes take effect constitutes
              acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">15. Contact Us</h2>
            <p>If you have questions about this Privacy Policy or our data practices, contact us:</p>
            <ul className="list-none mt-2 space-y-1">
              <li><strong>Email:</strong> tomaceo1983@gmail.com</li>
            </ul>
          </section>
        </div>

        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-center text-gray-500 text-sm">
            © 2026 HappyBook. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
